// What the kiosk says: kiosk.<key> in i18n/locales/<code>.json; any language
// without a file reads English.
//
// The customer chooses a language on the first screen and the whole flow follows
// it, including the service names, which come from the catalogue rather than from
// here. A missing translation falls back rather than showing a key: a button with
// no words is broken, a button in the wrong language is merely inconvenient.
import { LEGACY_CUSTOMER, nativeName, resolveText } from './i18n/customer'
import type { LanguageCode } from './i18n/languages'

import { wordText } from './i18n/catalog'
import { englishTable, type KioskKey } from './i18n/english'
import { mayNotBeServed, waitText, type Estimate } from './estimate'

/** A customer language (lib/i18n/languages.ts). */
export type Language = LanguageCode

/** The languages these words are written in. */
type Written = 'az' | 'ru' | 'en'

/** A language button per offered language, in order, labelled in itself. */
export function languageButtons(codes: readonly LanguageCode[]): { code: Language; label: string }[] {
  return codes.map((code) => ({ code, label: nativeName(code) }))
}

/** The three every organisation offered before languages were a setting. */
export const languages = languageButtons(LEGACY_CUSTOMER)

/** A kiosk word's key, without kiosk. (locales/en.json). */
export type PhraseKey = KioskKey

/** t returns the phrase in the chosen language (i18n/catalog.ts, key kiosk.<key>), English when it has none. */
export function t(key: PhraseKey, lang: Language): string {
  return wordText(`kiosk.${key}`, lang)
}

/** A kiosk word's shipped English (what an organisation's word is checked against). */
export function english(key: PhraseKey): string {
  return englishTable()[`kiosk.${key}`] ?? ''
}

/** The failed screen's words: a busy machine waits, a busy branch is tried again. */
export function failedWords(reason: string): { lead?: PhraseKey; aside?: PhraseKey; retry: boolean } {
  if (reason === 'TOO_MANY_TICKETS') return { lead: 'busy', aside: 'orSeeReception', retry: false }
  if (reason === 'BRANCH_BUSY') return { lead: 'branchBusy', retry: true }
  return { lead: 'unavailable', retry: true }
}

/** The written language to say a thing in: English for any we do not write. */
const asLanguage = (lang: string): Written => (lang === 'az' || lang === 'ru' ? lang : 'en')

/** "In queue: 5", in the board's language (English for any we do not write). */
export function inQueueLine(n: number, lang: string): string {
  return t('inQueue', asLanguage(lang)).replace('{n}', String(n))
}

/** "Valid until 18:00", in the slip's language; the time is already on the branch's clock.
 *  The same words cmd/qms-agent prints on paper. */
export function validUntilLine(time: string, lang: string): string {
  return t('validUntil', asLanguage(lang)).replace('{time}', time)
}

/** A desk's state as a counter display words it; a closed desk never says "Free". */
export function deskStatusLine(status: 'open' | 'paused' | 'closed' | undefined, lang: string): string {
  const key: PhraseKey = status === 'paused' ? 'deskBackSoon' : status === 'closed' ? 'deskClosed' : 'deskFree'
  return t(key, asLanguage(lang))
}

/** What a counter display's status pill says: the ticket's state while one is at the desk, else the desk's. */
export type DeskPill = 'called' | 'serving' | 'open' | 'paused' | 'closed'

export function deskPillLine(pill: DeskPill | undefined, lang: string): string {
  if (pill === 'called') return t('deskCalled', asLanguage(lang))
  if (pill === 'serving') return t('deskServing', asLanguage(lang))
  return deskStatusLine(pill, lang)
}

/**
 * The server's estimate in the customer's words: "9 min", "15–20 min", "over 2
 * hours", "—" with no desk, '' with none; and the late flag after it.
 */
export function estimateInWords(e: Estimate | null | undefined, lang: Language): string {
  const min = t('minutes', lang)
  const words = waitText(e, {
    min: (m) => `${m} ${min}`,
    range: (low, high) => `${low}–${high} ${min}`,
    over: t('overTwoHours', lang),
  })
  return words && mayNotBeServed(e) ? `${words} · ${t('mayNotBeServed', lang)}` : words
}

/**
 * How many are in front of this customer, from the position the server gave.
 *
 * Unknown is not zero. The server answers 0 when it could not count, and a
 * waiting ticket is always at least first — so 0, like a missing answer, means
 * say nothing, not "you are next".
 */
export function aheadFromPosition(position: number | undefined): number | undefined {
  if (position === undefined || position < 1) return undefined
  return position - 1
}

/** The ahead line on the issued screen, in the customer's language. */
export function aheadSentence(ahead: number, lang: Language): string {
  return ahead === 0 ? t('nextInLine', lang) : `${t('peopleAhead', lang)}: ${ahead}`
}

/**
 * When the doors open again, written out rather than formatted.
 *
 * toLocaleString with an az-AZ locale answered "Wed" on the machine this was tested
 * on: the browser's ICU has no Azerbaijani weekday names and falls back to
 * abbreviated English, so an Azerbaijani sentence ended in an English word. This
 * file already exists because the product does not trust the runtime for its words,
 * and seven day names in three languages is a smaller thing to carry than a
 * dependency on what ICU a lobby machine happened to ship with.
 *
 * Each language gets the whole tail, not just the day, because the grammar differs:
 * Azerbaijani places the day bare, Russian needs the accusative after "в" and
 * another "в" before the time, English wants "at".
 */
const weekdays: Record<Written, [string, string, string, string, string, string, string]> = {
  // Sunday first, matching Date.getDay().
  az: [
    'bazar',
    'bazar ertəsi',
    'çərşənbə axşamı',
    'çərşənbə',
    'cümə axşamı',
    'cümə',
    'şənbə',
  ],
  ru: ['воскресенье', 'понедельник', 'вторник', 'среду', 'четверг', 'пятницу', 'субботу'],
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
}

/** Sunday-first, matching both Date.getDay() and the order above. */
const enShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** The zone if this runtime knows it, nothing if it does not. */
function usableZone(timeZone: string | undefined): string | undefined {
  if (!timeZone) return undefined
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(0)
    return timeZone
  } catch {
    return undefined
  }
}

/**
 * `timeZone` is the branch's, and it matters.
 *
 * The doors open at nine o'clock *there*. Reading the instant in the machine's own
 * zone was wrong in three ways that all reached a screen: the build server rendered
 * a 09:00 Baku opening as 05:00 and failed the gate, an operator previewing a
 * branch from another country would read the wrong hour, and a lobby machine whose
 * clock was set up carelessly would put a wrong opening time on the wall — the one
 * thing on that screen a customer might plan a journey around.
 *
 * Falls back to the machine's own zone when the branch has not been given one,
 * which is the behaviour every installation had before branches carried a timezone.
 */
export function dayAndTime(iso: string, lang: Language, timeZone?: string): string {
  if (!iso) return ''
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''

  // Checked once, before it is used twice. Intl throws a RangeError on a zone name
  // it does not know, and a branch whose timezone was mistyped would otherwise take
  // the closed screen down — leaving a kiosk showing nothing at the one moment its
  // only job is to say the building is shut.
  const zone = usableZone(timeZone)

  // Which day it is is a question about a place, not only about an instant: 23:30
  // in Baku is the previous day in London. Asked through en-US, whose weekday names
  // are in every ICU build there is — unlike Azerbaijani's, which is why the names
  // above are written out here in the first place.
  let index = at.getDay()
  if (zone) {
    const short = new Intl.DateTimeFormat('en-US', { timeZone: zone, weekday: 'short' }).format(at)
    const found = enShort.indexOf(short)
    if (found >= 0) index = found
  }

  const written = asLanguage(lang)
  const day = weekdays[written][index] ?? ''
  // Twenty-four hour, stated rather than inferred: Azerbaijan writes 09:00, and a
  // machine whose browser is set to an American locale must not put "9 AM" on a
  // wall in Baku.
  const time = at.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(zone ? { timeZone: zone } : {}),
  })

  switch (written) {
    case 'ru':
      return `в ${day} в ${time}`
    case 'az':
      return `${day} ${time}`
    default:
      return `${day} at ${time}`
  }
}

/** The calendar date (YYYY-MM-DD) of an instant on the branch's clock. */
function dateIn(at: Date, zone: string | undefined): string {
  return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', ...(zone ? { timeZone: zone } : {}) }).format(at)
}

/**
 * The kiosk's closed line: "We open tomorrow at 09:00", with today and tomorrow
 * named and any later day by its weekday, all on the branch's clock.
 */
export function opensWords(iso: string, lang: Language, timeZone: string | undefined, now: Date): string {
  if (!iso) return ''
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''
  const zone = usableZone(timeZone)
  const time = clockIn(iso, zone)
  const day = dateIn(at, zone)
  const today = dateIn(now, zone)
  // Noon avoids a clock change moving "tomorrow" by a day.
  const noon = new Date(`${today}T12:00:00Z`)
  noon.setUTCDate(noon.getUTCDate() + 1)
  const tomorrow = noon.toISOString().slice(0, 10)
  const near = day === today ? 'today' : day === tomorrow ? 'tomorrow' : null
  switch (asLanguage(lang)) {
    case 'ru':
      if (near) return `Откроемся ${near === 'today' ? 'сегодня' : 'завтра'} в ${time}`
      return `Откроемся ${dayAndTime(iso, lang, zone)}`
    case 'en':
      if (near) return `We open ${near} at ${time}`
      return `We open ${dayAndTime(iso, 'en', zone)}`
    default:
      // "Açılış: sabah, 09:00": no case ending on the time, which would follow its
      // spoken vowel (09:00-da, 08:00-də).
      if (near) return `Açılış: ${near === 'today' ? 'bu gün' : 'sabah'}, ${time}`
      return `Açılış: ${dayAndTime(iso, lang, zone).replace(/ (\d\d:\d\d)$/, ', $1')}`
  }
}

/** The wall-clock time of an instant in the branch's zone, 24-hour. */
export function clockIn(iso: string, timeZone?: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return ''
  const zone = usableZone(timeZone)
  return at.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(zone ? { timeZone: zone } : {}),
  })
}

/** The weekday a business day (YYYY-MM-DD) falls on, in the customer's language —
 *  the form a sentence about "coming back on" it takes. Read as a calendar date,
 *  not an instant, so no zone can move it. */
export function bookingDay(businessDate: string, lang: Language): string {
  const [y, m, d] = businessDate.split('-').map(Number)
  if (!y || !m || !d) return businessDate
  return weekdays[asLanguage(lang)][new Date(Date.UTC(y, m - 1, d)).getUTCDay()] ?? businessDate
}

/** serviceName picks the catalogue name: the chosen language, the organisation's default, English, any. */
export function serviceName(name: Record<string, string>, lang: Language, fallback: string, orgDefault?: Language): string {
  return resolveText(name, lang, orgDefault) || fallback
}
